/**
 * The marketing-campaign gate as `GET /auth/me?includes=campaignIntegration` returns it.
 *
 * Declared as the minimal shape this calculation reads rather than imported from the
 * generated client, the same way `PresaleRoutePlace` is in `presale-route.ts` — depend on
 * the shape, not the type.
 */
export type CampaignGate = {
  /** Checkout flow the campaign belongs to (`"presale"` today). */
  flow: string;
  /** ISO 8601 instant the caller's access opens. */
  delayUntil: string;
};

/**
 * Whether the caller is still locked out by a campaign gate.
 *
 * The platform already drops the field once the gate expires, so a non-null value is the
 * answer. This re-checks `now < delayUntil` anyway — mirroring `readActivePresaleSource`
 * on the platform — because the `/auth/me` response is CACHED: a session left open across
 * launch day would otherwise stay locked on a stale answer until the next refetch.
 *
 * An unparseable `delayUntil` opens the app. Bad data must never lock someone out forever,
 * and it is the same direction the platform's reader takes.
 */
export function isCampaignGateOpen(
  gate: CampaignGate | null | undefined,
  now: Date,
): boolean {
  if (!gate) return false;

  const delayUntil = Date.parse(gate.delayUntil);
  if (Number.isNaN(delayUntil)) return false;

  return now.getTime() < delayUntil;
}
